/**
 * Os documentos que os passos da captação exigem (`TP-057`). O upload e as ações devolvem a visão ATUALIZADA, já
 * mapeada. A leitura do documento NÃO acontece na chamada: o upload só registra, e a próxima rodada lê — por isso a tela
 * diz "aguardando leitura" em vez de esperar um veredito.
 */

import { queryOptions } from '@tanstack/react-query';
import type { Schemas } from '@anery/api-contracts';

import { montarUrlDaApi, requisitar } from '../../../shared/api/clienteHttp';
import type {
  AcaoNoDocumento,
  DocumentoDoCaso,
  DocumentoExigido,
  CampoDoCadastro,
  DadoDoCaso,
  DocumentosDoCaso,
  EstadoDaLeitura,
  EstadoDoDocumento,
  VereditoDaLeitura,
} from './tipos';

type Fio = Schemas['DocumentosDoCasoResponse'];

/** O arquivo do documento, para a pessoa abrir antes de validar. A sessão por cookie resolve pela navegação normal. */
export function urlDoArquivoDoDocumento(casoId: string, documentoId: string): string {
  return montarUrlDaApi(`/casos/${encodeURIComponent(casoId)}/documentos/${encodeURIComponent(documentoId)}/arquivo`);
}

export const chavesDeDocumentos = {
  doCaso: (casoId: string) => ['casos', casoId, 'documentos'] as const,
};

function mapearExigido(f: Schemas['DocumentoExigidoResponse']): DocumentoExigido {
  return {
    id: f.id,
    frente: f.frente ?? '',
    passo: f.passo,
    passoRotulo: f.passoRotulo ?? '',
    item: f.item ?? '',
    rotulo: f.rotulo ?? f.item ?? '',
    obrigatorio: f.obrigatorio,
    estado: (f.estado ?? 'pendente') as EstadoDoDocumento,
    fechado: f.fechado,
    exigeConferencia: f.exigeConferencia,
    veredito: (f.veredito ?? null) as VereditoDaLeitura | null,
    motivo: f.motivo ?? null,
    motivoDaPessoa: f.motivoDaPessoa ?? null,
    documentoId: f.documentoId ?? null,
    nomeDoDocumento: f.nomeDoDocumento ?? null,
    exigencias: (f.exigencias ?? []).map((e) => ({
      chave: e.chave ?? '',
      descricao: e.descricao ?? e.chave ?? '',
      atendida: e.atendida ?? null,
    })),
  };
}

function mapearDocumento(f: Schemas['DocumentoDoCasoResponse']): DocumentoDoCaso {
  return {
    id: f.id,
    origem: (f.origem ?? 'upload') as DocumentoDoCaso['origem'],
    nomeArquivo: f.nomeArquivo ?? '',
    leitura: (f.leitura ?? 'pendente') as EstadoDaLeitura,
    registradoEm: new Date(f.registradoEm),
    ultimoVeredito: (f.ultimoVeredito ?? null) as VereditoDaLeitura | null,
    ultimoMotivo: f.ultimoMotivo ?? null,
    itemPretendidoId: f.itemPretendidoId ?? null,
    ultimoItemId: f.ultimoItemId ?? null,
    descartadoEm: f.descartadoEm ? new Date(f.descartadoEm) : null,
    metadados: (f.metadados ?? []).map((m) => ({
      chave: m.chave ?? '',
      rotulo: m.rotulo ?? m.chave ?? '',
      valor: m.valor ?? '',
      origem: m.origem === 'regra' ? ('regra' as const) : ('agente' as const),
    })),
    tipoLido: f.tipoLido ?? null,
  };
}

function mapearDado(f: Schemas['DadoDoCasoResponse']): DadoDoCaso {
  return {
    chave: f.chave ?? '',
    rotulo: f.rotulo ?? f.chave ?? '',
    cadastro: (f.cadastro ?? null) as CampoDoCadastro | null,
    diverge: f.diverge,
    valores: (f.valores ?? []).map((v) => ({
      valor: v.valor ?? '',
      fontes: (v.fontes ?? []).map((s) => ({ documentoId: s.documentoId, arquivo: s.arquivo ?? '', item: s.item ?? '' })),
    })),
  };
}

export function mapearDocumentosDoCaso(fio: Fio): DocumentosDoCaso {
  return {
    exigidos: (fio.exigidos ?? []).map(mapearExigido),
    documentos: (fio.documentos ?? []).map(mapearDocumento),
    dados: (fio.dados ?? []).map(mapearDado),
  };
}

export function documentosDoCaso(casoId: string) {
  return queryOptions({
    queryKey: chavesDeDocumentos.doCaso(casoId),
    queryFn: ({ signal }) =>
      requisitar<Fio>(`/casos/${encodeURIComponent(casoId)}/documentos`, { sinal: signal }).then(
        mapearDocumentosDoCaso,
      ),
    // A leitura não acontece no envio: roda logo depois dele, ou na próxima rodada do ciclo (60 s). Enquanto algum
    // arquivo espera, a tela pergunta de novo a cada 15 s, para o veredito aparecer sem a pessoa recarregar a página;
    // perguntar mais vezes que isso só repete a mesma resposta.
    refetchInterval: (consulta) =>
      (consulta.state.data?.documentos ?? []).some((d) => d.leitura === 'pendente') ? 15_000 : false,
    refetchOnWindowFocus: true,
  });
}

/**
 * `POST /casos/{id}/documentos-exigidos/{exigidoId}/acao`. Dispensar e reabrir exigem motivo (`400`); conferir exige um
 * documento no item (`409`). O ator NÃO vai no corpo: o back o lê da sessão.
 */
export function agirNoDocumento(
  casoId: string,
  exigidoId: string,
  acao: AcaoNoDocumento,
  motivo?: string,
  confirmado?: boolean,
): Promise<DocumentosDoCaso> {
  return requisitar<Fio>(
    `/casos/${encodeURIComponent(casoId)}/documentos-exigidos/${encodeURIComponent(exigidoId)}/acao`,
    { method: 'POST', corpo: { acao, motivo: motivo ?? null, confirmado: confirmado ?? false } },
  ).then(mapearDocumentosDoCaso);
}

/** `POST /casos/{id}/documentos/{documentoId}/atribuir` — "este documento é do item X". */
export function atribuirDocumento(casoId: string, documentoId: string, exigidoId: string): Promise<DocumentosDoCaso> {
  return requisitar<Fio>(
    `/casos/${encodeURIComponent(casoId)}/documentos/${encodeURIComponent(documentoId)}/atribuir`,
    { method: 'POST', corpo: { exigidoId } },
  ).then(mapearDocumentosDoCaso);
}

/**
 * `POST /casos/{id}/documentos/{documentoId}/descartar` (ou `/restaurar`). Só o solto já lido se descarta: o que sustenta
 * um item ou espera a leitura volta `409`.
 */
export function descartarDocumento(casoId: string, documentoId: string, descartar: boolean): Promise<DocumentosDoCaso> {
  return requisitar<Fio>(
    `/casos/${encodeURIComponent(casoId)}/documentos/${encodeURIComponent(documentoId)}/${descartar ? 'descartar' : 'restaurar'}`,
    { method: 'POST' },
  ).then(mapearDocumentosDoCaso);
}

/**
 * `POST /casos/{id}/documentos` (multipart). Com `exigidoId` o envio é NO item ("este é o PAD"); sem ele, solto. O arquivo
 * que passa do teto volta `413`; o mesmo arquivo duas vezes volta o mesmo documento (`criado: false`).
 */
/** `criado: false` quando o MESMO arquivo já estava no Caso: nada novo é registrado nem lido de novo. */
export function enviarDocumento(
  casoId: string,
  arquivo: File,
  exigidoId?: string,
): Promise<{ criado: boolean; documentoId: string; voltouParaLeitura: boolean }> {
  const forma = new FormData();
  forma.append('file', arquivo);
  if (exigidoId !== undefined) forma.append('exigidoId', exigidoId);
  return requisitar<Schemas['DocumentoEnviadoResponse']>(`/casos/${encodeURIComponent(casoId)}/documentos`, {
    method: 'POST',
    corpo: forma,
  }).then((r) => ({ criado: r.criado, documentoId: r.documentoId, voltouParaLeitura: r.voltouParaLeitura }));
}
